// components/common/modals/UsersTransactionModal.tsx
import React, { useState } from "react";
import useToken from "../../../../hooks/auth/useToken";
import usePaymentsByUser from "../../../../hooks/administration/transactions/useUserPayments";
import useUser from "../../../../hooks/administration/users/useUser";
import { paymentsService } from "../../../../services/administration/paymentsService";
import BaseModal from "../../../ui/Modal";
import { Payment } from "../../../../types/features/administration/Transaction";
import TransactionsList from "../transactions/TransactionsList";
import TransactionDetailsModal from "../transactions/TransactionDetails";


interface FilterState {
  search: string;
  status: string;
  operator: string;
  provider: string;
  dateRange: string;
  startDate: string;
  endDate: string;
}

interface UsersTransactionModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  userName?: string;
  onTransactionUpdated?: () => void;
}

const UsersTransactionModal: React.FC<UsersTransactionModalProps> = ({
  isOpen,
  onClose,
  userId,
  userName,
  onTransactionUpdated,
}) => {
  const { token } = useToken();
  const [selectedTransaction, setSelectedTransaction] = useState<Payment | null>(null);

  const {
    payments: apiPayments,
    loading: paymentsLoading,
    getPayments: refreshPayments,
    total: totalTransactionsCount,
    currentPage: serverPage,
    totalPages: serverTotalPages,
    goToPage,
    handleSearch: serverSearch,
  } = usePaymentsByUser({
    userId: userId!,
    enabled: isOpen,
  });

  const { user } = useUser({ userId: userId || "" });
  console.log(user);

  const [filters, setFilters] = useState<FilterState>({
    search: "",
    status: "",
    operator: "",
    provider: "",
    dateRange: "",
    startDate: "",
    endDate: "",
  });

  // Filtrage client-side
  const filteredTransactions = apiPayments.filter((transaction) => {
    return (
      (filters.status === "" || transaction.status === filters.status) &&
      (filters.operator === "" || transaction.operator === filters.operator) &&
      (filters.provider === "" || transaction.provider === filters.provider)
    );
  });

  const handleFiltersChange = (newFilters: FilterState) => {
    if (newFilters.search !== filters.search) {
      serverSearch(newFilters.search);
    }
    setFilters(newFilters);
  };

  const handleTransactionAction = async (action: string, transaction: Payment, data?: any) => {
    switch (action) {
      case "view":
        setSelectedTransaction(transaction);
        break;
      case "change-status":
        try {
          const payoutStatus = data?.payout_status || "SUCCESS";
          const transactionStatus = data?.transaction_status || "SUCCEEDED";
          const response = await paymentsService.changePaymentStatus(
            token,
            transaction.id,
            payoutStatus,
            transactionStatus
          );
          if (response.message) {
            alert(`✅ ${response.message}`);
            refreshPayments();
            onTransactionUpdated?.();
          }
        } catch (error: any) {
          console.error("Erreur:", error);
          alert(`❌ Erreur: ${error.response?.data?.message || error.message}`);
        }
        break;
      default:
        console.log("Action:", action, transaction);
    }
  };

  const startIndex = (serverPage - 1) * 20;
  const endIndex = Math.min(startIndex + filteredTransactions.length, totalTransactionsCount);

  return (
    <>
      <BaseModal isOpen={isOpen} onClose={onClose} title={`Transactions de ${userName ||
        "l'utilisateur"}`}  size="full">
        <div className="space-y-4">
          {/* Statistique rapide */}
          <div className="bg-blue-50 rounded-lg p-3">
            <div className="text-sm text-blue-600">Etats des transactions</div>
            {user && (
              <ul className="space-y-1 text-sm">
                <li>
                  <b>Total envoyé :</b> {user.transaction_stats?.total_sent ?? 0}{" "}
                  {user.transaction_stats?.total_sent_currency}
                </li>

                <li>
                  <b>Total en CAD :</b> {user.transaction_stats?.total_sent_cad ?? 0} CAD
                </li>

                <li>
                  <b>Nombre de transferts :</b>{" "}
                  {user.transaction_stats?.transaction_count || totalTransactionsCount || 0}
                </li>
              </ul>
            )}
          </div>

          {/* Liste des Transactions avec scroll horizontal */}
          <div className="overflow-x-auto -mx-6 px-6">
            <TransactionsList
              transactions={filteredTransactions}
              filters={filters}
              onFiltersChange={handleFiltersChange}
              onTransactionAction={handleTransactionAction}
              currentPage={serverPage}
              totalPages={serverTotalPages}
              onPageChange={goToPage}
              startIndex={startIndex}
              endIndex={endIndex}
              totalTransactions={totalTransactionsCount}
              loading={paymentsLoading}
            />
          </div>

        </div>
      </BaseModal>


      {/* Modal des détails d'une transaction */}
      <TransactionDetailsModal
        transaction={selectedTransaction}
        isOpen={!!selectedTransaction}
        onClose={() => setSelectedTransaction(null)}
        onTransactionUpdated={() => {
          refreshPayments();
          onTransactionUpdated?.();
        }}
      />
    </>
  );
};

export default UsersTransactionModal;
